// Pure adapters between the REST wire format and the shapes the pages
// consume. No fetch, no env, no React — so they are trivially testable and
// api.ts stays a thin transport.

import type {
  Envelope,
  IngestionEvent,
  IngestionStatus,
  ListRoastsResponse,
  RoastPageData,
  SyncHealth,
  Timeseries,
} from "./types";

// ---- List envelope --------------------------------------------------------

// `total`, `truncated` and `next_cursor` are what make a page a page
// (issue #46). They used to be dropped here, which left the list screen
// unable to say "50 of 224" or fetch the rest.
export function envelopeToRoastList(
  env: Envelope<Partial<RoastPageData>>,
): ListRoastsResponse {
  const rows = env.data.rows ?? [];
  const count = env.data.count ?? rows.length;
  return {
    summary: env.text ?? "",
    roasts: rows,
    count,
    total: env.data.total ?? count,
    truncated: env.data.truncated ?? false,
    next_cursor: env.data.next_cursor ?? null,
  };
}

// ---- Time series ------------------------------------------------------------

// Shape the timeseries handler returns inside `data`. Row-oriented; one
// `ProfilePoint.model_dump()` per sample.
export interface TimeseriesData {
  roast_id: string;
  start_s: number;
  end_s: number;
  max_points: number;
  count: number;
  points: Array<{
    time_s: number;
    bt?: number | null;
    ibts?: number | null;
    et?: number | null;
    ror_bt?: number | null;
    ror_ibts?: number | null;
    power?: number | null;
    fan?: number | null;
    drum?: number | null;
  }>;
}

// Recharts wants one array per series, so transpose. Every channel the
// backend sends is kept — power, fan and drum included, because the chart
// draws the control band from them.
export function transposeTimeseries(data: TimeseriesData): Timeseries {
  const pts = data.points ?? [];
  const elapsed_time_s: number[] = [];
  const bean_temp: (number | null)[] = [];
  const ibts_temp: (number | null)[] = [];
  const environmental_temp: (number | null)[] = [];
  const ror_bt: (number | null)[] = [];
  const ror_ibts: (number | null)[] = [];
  const ror: (number | null)[] = [];
  const power_setting: (number | null)[] = [];
  const fan_setting: (number | null)[] = [];
  const drum_setting: (number | null)[] = [];
  let fromBt = 0;
  let fromIbts = 0;
  for (const p of pts) {
    elapsed_time_s.push(p.time_s);
    bean_temp.push(p.bt ?? null);
    ibts_temp.push(p.ibts ?? null);
    environmental_temp.push(p.et ?? null);
    ror_bt.push(p.ror_bt ?? null);
    ror_ibts.push(p.ror_ibts ?? null);
    // Prefer bean-probe RoR; fall back to IBTS RoR when the probe channel
    // is missing for this sample.
    if (p.ror_bt != null) {
      ror.push(p.ror_bt);
      fromBt += 1;
    } else if (p.ror_ibts != null) {
      ror.push(p.ror_ibts);
      fromIbts += 1;
    } else {
      ror.push(null);
    }
    power_setting.push(p.power ?? null);
    fan_setting.push(p.fan ?? null);
    drum_setting.push(p.drum ?? null);
  }
  const ror_source: Timeseries["ror_source"] =
    fromBt === 0 && fromIbts === 0 ? null : fromBt >= fromIbts ? "bt" : "ibts";
  return {
    roast_id: data.roast_id,
    start_s: data.start_s,
    end_s: data.end_s,
    max_points: data.max_points,
    count: data.count ?? pts.length,
    elapsed_time_s,
    bean_temp,
    ibts_temp,
    environmental_temp,
    ror_bt,
    ror_ibts,
    ror,
    ror_source,
    power_setting,
    fan_setting,
    drum_setting,
  };
}

// ---- Ingestion status -------------------------------------------------------

export interface IngestionStatusData {
  entries?: Array<Record<string, unknown>>;
  counts?: Record<string, number>;
  stale_detector_records?: number | null;
  current_detector_version?: number;
  sync?: SyncHealth | null;
}

// Local SQLite entries carry `at`/`message`; cloud entries carry
// `timestamp`/`reason`. Fold both into one shape and keep everything else
// the handler reports (issue #7 staleness, issue #34 sync health).
export function adaptIngestionStatus(
  env: Envelope<IngestionStatusData>,
): IngestionStatus {
  const events: IngestionEvent[] = (env.data.entries ?? []).map((e) => ({
    source_file: (e.source_file as string | null) ?? null,
    source_hash: (e.source_hash as string | null) ?? null,
    status: (e.status as string) ?? "unknown",
    reason:
      (e.reason as string | null) ??
      (e.message as string | null) ??
      null,
    roast_id: (e.roast_id as string | null) ?? null,
    timestamp:
      (e.timestamp as string | null) ??
      (e.at as string | null) ??
      null,
    detector_version:
      e.detector_version == null ? null : String(e.detector_version),
  }));
  const last = events.find((e) => e.timestamp)?.timestamp ?? null;
  return {
    summary: env.text ?? "",
    recent_events: events,
    counts: env.data.counts ?? {},
    last_ingested_at: last,
    stale_detector_records: env.data.stale_detector_records ?? null,
    current_detector_version: env.data.current_detector_version ?? 0,
    sync: env.data.sync ?? null,
  };
}
